import React, { useState, useEffect } from 'react';
import { GraduationCap, BookOpen, School, Users, Code, Brain, Zap, Award, ChevronRight, Sparkles } from 'lucide-react';
import { Modal, Form, Button, Row, Col } from 'react-bootstrap';
import Lab from '../images/AI-Kit-1.jpg';
import emailjs from '@emailjs/browser'; // Import EmailJS

const AITrainerKitSection = () => {
  const [hoveredCard, setHoveredCard] = useState(null);
  const [isVisible, setIsVisible] = useState(false);
  const [animatedElements, setAnimatedElements] = useState([]);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  
  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [selectedKit, setSelectedKit] = useState(null);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    institutionName: '',
    institutionType: '',
    position: '',
    specificRequirements: '',
  });

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  useEffect(() => {
    setIsVisible(true);
    // Floating bubbles logic...
    const elements = Array.from({ length: 100 }, (_, i) => ({
      id: i,
      x: Math.random() * 150 - 25,
      y: Math.random() * 100,
      size: Math.random() * 20 + 5,
      duration: Math.random() * 12 + 8,
      delay: Math.random() * 6,
      direction: Math.random() > 0.5 ? 1 : -1,
      opacity: Math.random() * 0.5 + 0.15,
      color: ['#14b8a6', '#8B5CF6', '#F97316', '#22C55E', '#3B82F6', '#EF4444', '#F59E0B', '#EC4899', '#10B981', '#6366F1'][Math.floor(Math.random() * 10)],
      type: Math.random() > 0.6 ? 'bubble' : 'dot',
      blur: Math.random() * 3 + 1
    }));
    setAnimatedElements(elements);
  }, []);

  const trainerKitData = [
    {
      id: 'universities',
      title: 'Universities',
      icon: GraduationCap,
      description: 'Advanced research programs, graduate-level AI courses, and faculty development to foster cutting-edge AI research and innovation.',
      color: '#8B5CF6',
      features: ['Research Programs', 'Graduate Courses', 'Faculty Development', 'Innovation Labs'],
      badge: 'Advanced',
      stats: 'Research Focused'
    },
    {
      id: 'colleges',
      title: 'Colleges',
      icon: BookOpen,
      description: 'Undergraduate programs, practical AI training, and industry-ready curriculum designed to prepare students for AI careers.',
      color: '#22C55E',
      features: ['Undergraduate Programs', 'Practical Training', 'Industry Curriculum', 'Career Prep'],
      badge: 'Professional',
      stats: 'Industry Ready'
    },
    {
      id: 'schools',
      title: 'Schools',
      icon: School,
      description: 'Introduction to AI concepts, age-appropriate content, and teacher training to build foundational AI literacy from an early age.',
      color: '#F97316',
      features: ['AI Concepts', 'Age-Appropriate', 'Teacher Training', 'Foundation Building'],
      badge: 'Foundational',
      stats: 'Future Ready'
    }
  ];

  // Modal handlers
  const handleShowModal = (kit) => {
    setSelectedKit(kit);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedKit(null);
    setFormData({
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      institutionName: '',
      institutionType: '',
      position: '',
      specificRequirements: '',
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Updated handleSubmit function
  const handleSubmit = (e) => {
    e.preventDefault();

    // 1. Dynamically build the email body as a plain string
    const emailBody = `
Inquiry for AI Trainer Kit: ${selectedKit?.title || 'Not specified'}

Contact Details:
- Name: ${formData.firstName} ${formData.lastName}
- Email: ${formData.email}
- Phone: ${formData.phone || 'Not provided'}

Institution Details:
- Institution: ${formData.institutionName}
- Type: ${formData.institutionType}
- Position: ${formData.position}

Specific Requirements:
${formData.specificRequirements || 'None'}
    `;

    // 2. Prepare the parameters for the universal template
    const templateParams = {
      email_subject: 'AI Trainer Kit Query', // Specific subject for this form
      email_body: emailBody.trim()
    };

    // 3. Use your EmailJS credentials
    const serviceID = 'service_n36e8hg';
    const templateID = 'template_i9jkfri';
    const publicKey = 'VAEdCMXuUKeCOw6gb';

    emailjs.send(serviceID, templateID, templateParams, publicKey)
      .then((response) => {
        console.log('SUCCESS!', response.status, response.text);
        alert('Thank you for your interest! We will contact you soon.');
        handleCloseModal();
      })
      .catch((err) => {
        console.error('FAILED...', err);
        alert('Failed to send the request. Please try again later.');
      });
  };

  // [Styles object remains the same...]
  const styles = {
    container: {
      color: '#1e293b',
      padding: '6rem 1rem',
      position: 'relative',
      overflow: 'hidden',
      minHeight: '100vh',
    },
    floatingElements: {
      position: 'absolute',
      inset: 0,
      pointerEvents: 'none',
      zIndex: 1
    },
    floatingElement: {
      position: 'absolute',
      borderRadius: '50%',
      animation: 'bubbleFloat 10s ease-in-out infinite'
    },
    header: {
      textAlign: 'center',
      marginBottom: '4rem',
      position: 'relative',
      zIndex: 10
    },
    title: {
      fontSize: 'clamp(2.5rem, 8vw, 4rem)',
      fontWeight: '900',
      marginBottom: '1.5rem',
      background: 'linear-gradient(135deg, #14b8a6 0%, #8B5CF6 50%, #F97316 100%)',
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
      backgroundClip: 'text',
      lineHeight: '1.2',
      letterSpacing: '-0.02em',
      transform: isVisible ? 'translateY(0)' : 'translateY(48px)',
      opacity: isVisible ? 1 : 0,
      transition: 'all 1s ease',
      textShadow: '0 4px 20px rgba(0,0,0,0.1)'
    },
    subtitle: {
      fontSize: 'clamp(1rem, 2.5vw, 1.25rem)',
      color: '#64748b',
      maxWidth: '800px',
      margin: '0 auto',
      lineHeight: '1.75',
      transform: isVisible ? 'translateY(0)' : 'translateY(48px)',
      opacity: isVisible ? 1 : 0,
      transition: 'all 1s ease 0.3s',
      padding: '0 1rem'
    },
    imageContainer: {
      width: '90%',
      height: '25em',
      borderRadius: '25px',
      margin: '3em auto 0',
      display: 'block',
      boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
      border: '3px solid rgba(20, 184, 166, 0.2)',
      maxWidth: '800px',
      objectFit: 'cover'
    },
    grid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))',
      gap: '2rem',
      maxWidth: '1400px',
      margin: '0 auto',
      position: 'relative',
      zIndex: 10,
      padding: '0 1rem'
    },
    card: {
      background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(248, 250, 252, 0.8) 100%)',
      borderRadius: '24px',
      padding: 'clamp(1.5rem, 4vw, 2.5rem)',
      border: '2px solid rgba(148, 163, 184, 0.2)',
      backdropFilter: 'blur(20px)',
      position: 'relative',
      overflow: 'hidden',
      cursor: 'pointer',
      transform: isVisible ? 'translateY(0)' : 'translateY(48px)',
      opacity: isVisible ? 1 : 0,
      transition: 'all 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
      minHeight: 'clamp(450px, 60vh, 500px)',
      display: 'flex',
      flexDirection: 'column',
      boxShadow: '0 10px 40px rgba(0,0,0,0.1)',
      width: '100%'
    },
    cardGlow: {
      position: 'absolute',
      inset: 0,
      borderRadius: '24px',
      padding: '2px',
      mask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
      maskComposite: 'exclude',
      opacity: 0,
      transition: 'opacity 0.3s ease'
    },
    cardContent: {
      position: 'relative',
      zIndex: 2,
      height: '100%',
      display: 'flex',
      flexDirection: 'column'
    },
    cardHeader: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      marginBottom: '2rem',
      flexWrap: 'wrap',
      gap: '1rem'
    },
    iconContainer: {
      width: 'clamp(60px, 15vw, 80px)',
      height: 'clamp(60px, 15vw, 80px)',
      borderRadius: '20px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      position: 'relative',
      transition: 'all 0.3s ease',
      boxShadow: '0 8px 25px rgba(0,0,0,0.15)',
      flexShrink: 0
    },
    iconBackground: {
      position: 'absolute',
      inset: 0,
      borderRadius: '20px',
      background: 'linear-gradient(135deg, rgba(20, 184, 166, 0.15), rgba(139, 92, 246, 0.15))',
      transition: 'all 0.3s ease'
    },
    badge: {
      padding: '0.5rem 1rem',
      borderRadius: '20px',
      fontSize: 'clamp(0.625rem, 2vw, 0.75rem)',
      fontWeight: '600',
      border: '2px solid rgba(20, 184, 166, 0.3)',
      boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
      whiteSpace: 'nowrap'
    },
    cardTitle: {
      fontSize: 'clamp(1.5rem, 4vw, 2rem)',
      fontWeight: '700',
      marginBottom: '1rem',
      transition: 'color 0.3s ease'
    },
    cardDescription: {
      color: '#64748b',
      fontSize: 'clamp(0.875rem, 2.5vw, 1rem)',
      lineHeight: '1.75',
      marginBottom: '2rem',
      flex: 1
    },
    featuresGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
      gap: '1rem',
      marginBottom: '2rem'
    },
    feature: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.75rem',
      padding: 'clamp(0.5rem, 2vw, 0.75rem)',
      background: 'rgba(248, 250, 252, 0.8)',
      borderRadius: '12px',
      border: '1px solid rgba(148, 163, 184, 0.2)',
      transition: 'all 0.3s ease',
      boxShadow: '0 2px 10px rgba(0,0,0,0.05)'
    },
    featureIcon: {
      width: '32px',
      height: '32px',
      borderRadius: '8px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0
    },
    featureText: {
      fontSize: 'clamp(0.75rem, 2vw, 0.875rem)',
      color: '#475569',
      fontWeight: '500',
      lineHeight: '1.3'
    },
    cardFooter: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      marginTop: 'auto',
      paddingTop: '1.5rem',
      borderTop: '1px solid rgba(148, 163, 184, 0.2)',
      flexWrap: 'wrap',
      gap: '1rem'
    },
    stats: {
      fontSize: 'clamp(1rem, 2.5vw, 1.125rem)',
      fontWeight: '600',
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem'
    },
    ctaButton: {
      border: 'none',
      borderRadius: '12px',
      padding: 'clamp(0.625rem, 2vw, 0.75rem) clamp(1rem, 3vw, 1.5rem)',
      color: 'white',
      fontWeight: '600',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      transition: 'all 0.3s ease',
      fontSize: 'clamp(0.75rem, 2vw, 0.875rem)',
      boxShadow: '0 4px 15px rgba(20, 184, 166, 0.3)',
      whiteSpace: 'nowrap'
    },
    bottomSection: {
      marginTop: '4rem',
      textAlign: 'center',
      position: 'relative',
      zIndex: 10,
      padding: '0 1rem'
    },
    trustBadge: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: 'clamp(1rem, 4vw, 2rem)',
      background: 'rgba(255, 255, 255, 0.9)',
      padding: 'clamp(1rem, 3vw, 1.5rem) clamp(1.5rem, 5vw, 3rem)',
      borderRadius: '50px',
      border: '2px solid rgba(20, 184, 166, 0.2)',
      backdropFilter: 'blur(20px)',
      boxShadow: '0 10px 40px rgba(0,0,0,0.1)',
      flexWrap: 'wrap',
      justifyContent: 'center',
      maxWidth: '100%'
    },
    trustItem: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.75rem'
    },
    trustDot: {
      width: '8px',
      height: '8px',
      borderRadius: '50%',
      background: 'linear-gradient(135deg, #14b8a6, #8B5CF6)',
      animation: 'pulse 2s infinite',
      flexShrink: 0
    },
    trustText: {
      color: '#64748b',
      fontSize: 'clamp(0.75rem, 2vw, 0.875rem)',
      fontWeight: '500',
      whiteSpace: 'nowrap'
    },
    divider: {
      width: '1px',
      height: '20px',
      background: 'rgba(148, 163, 184, 0.3)',
      display: windowWidth > 640 ? 'block' : 'none'
    }
  };

  const getFeatureIcon = (feature) => {
    const iconMap = { 
      'Research Programs': Brain, 
      'Graduate Courses': GraduationCap, 
      'Faculty Development': Users, 
      'Innovation Labs': Zap, 
      'Undergraduate Programs': BookOpen, 
      'Practical Training': Code, 
      'Industry Curriculum': Award, 
      'Career Prep': Users, 
      'AI Concepts': Brain, 
      'Age-Appropriate': School, 
      'Teacher Training': Users, 
      'Foundation Building': Award 
    };
    return iconMap[feature] || Brain;
  };

  return (
    <>
      <div style={styles.container} data-component="container">
        <div style={styles.floatingElements}>
          {animatedElements.map((element) => (
            <div
              key={element.id}
              style={{ 
                ...styles.floatingElement, 
                left: `${element.x}%`, 
                top: `${element.y}%`, 
                width: `${element.size}px`, 
                height: `${element.size}px`, 
                background: element.type === 'bubble' ? `radial-gradient(circle at 30% 30%, ${element.color}40, ${element.color}15, transparent 80%)` : `linear-gradient(45deg, ${element.color}35, ${element.color}55)`, 
                opacity: element.opacity, 
                animationDuration: `${element.duration}s`, 
                animationDelay: `${element.delay}s`, 
                transform: `scale(${element.direction > 0 ? 1 : 0.8})`, 
                border: element.type === 'bubble' ? `1px solid ${element.color}25` : 'none', 
                boxShadow: element.type === 'bubble' ? `0 0 ${element.size * 1.5}px ${element.color}15, inset 0 0 ${element.size * 0.8}px ${element.color}08` : `0 0 15px ${element.color}20`, 
                filter: `blur(${element.blur}px)` 
              }}
            />
          ))}
        </div>

        <div style={styles.header}>
          <h2 style={styles.title}>AI Trainer Kit</h2>
          <p style={styles.subtitle}>
            It is a first-of-its-kind, portable AI learning device powered by Intel, designed to empower students and educators across the world. Whether you're in a classroom, lab, innovation hub, or even a remote learning setup — this Intel-powered, plug-and-play kit delivers hands-on, real-world AI training on the go.
          </p>
          <img src={Lab} alt="AI Training Lab" style={styles.imageContainer} data-component="imageContainer" />
        </div>

        <div style={styles.grid} data-component="grid">
          {trainerKitData.map((item, index) => (
            <div
              key={item.id}
              data-component="card"
              style={{ 
                ...styles.card, 
                transitionDelay: `${index * 0.2}s`, 
                transform: hoveredCard === item.id ? 'translateY(-12px) scale(1.02)' : isVisible ? 'translateY(0)' : 'translateY(48px)', 
                boxShadow: hoveredCard === item.id ? `0 20px 60px ${item.color}20, 0 0 40px ${item.color}10` : '0 10px 40px rgba(0,0,0,0.1)' 
              }}
              onMouseEnter={() => setHoveredCard(item.id)}
              onMouseLeave={() => setHoveredCard(null)}
            >
              <div style={{ ...styles.cardGlow, opacity: hoveredCard === item.id ? 1 : 0, background: `linear-gradient(135deg, ${item.color}60, ${item.color}40)` }} />
              <div style={styles.cardContent}>
                <div style={styles.cardHeader} data-component="cardHeader">
                  <div style={styles.iconContainer}>
                    <div style={{ ...styles.iconBackground, background: hoveredCard === item.id ? `linear-gradient(135deg, ${item.color}20, ${item.color}30)` : styles.iconBackground.background }} />
                    <item.icon size={windowWidth < 768 ? 24 : 36} color={hoveredCard === item.id ? item.color : '#14b8a6'} style={{ position: 'relative', zIndex: 1 }} />
                  </div>
                  <div style={{ ...styles.badge, background: hoveredCard === item.id ? `linear-gradient(135deg, ${item.color}10, ${item.color}20)` : undefined, borderColor: hoveredCard === item.id ? `${item.color}50` : 'rgba(20, 184, 166, 0.3)', color: hoveredCard === item.id ? item.color : '#0f766e' }}>
                    {item.badge}
                  </div>
                </div>
                <h3 style={{ ...styles.cardTitle, color: hoveredCard === item.id ? item.color : '#1e293b' }}>{item.title}</h3>
                <p style={styles.cardDescription}>{item.description}</p>
                <div style={styles.featuresGrid} data-component="featuresGrid">
                  {item.features.map((feature, featureIndex) => {
                    const FeatureIcon = getFeatureIcon(feature);
                    return (
                      <div key={featureIndex} style={{ ...styles.feature, transform: hoveredCard === item.id ? 'scale(1.02)' : 'scale(1)', borderColor: hoveredCard === item.id ? `${item.color}30` : 'rgba(148, 163, 184, 0.2)', background: hoveredCard === item.id ? `${item.color}05` : 'rgba(248, 250, 252, 0.8)' }}>
                        <div style={{ ...styles.featureIcon, backgroundColor: `${item.color}15` }}>
                          <FeatureIcon size={16} color={item.color} />
                        </div>
                        <span style={styles.featureText}>{feature}</span>
                      </div>
                    );
                  })}
                </div>
                <div style={styles.cardFooter} data-component="cardFooter">
                  <div style={{ ...styles.stats, color: hoveredCard === item.id ? item.color : '#0f766e' }}>
                    <Sparkles size={16} />
                    {item.stats}
                  </div>
                  <button 
                    data-component="ctaButton" 
                    style={{ 
                      ...styles.ctaButton, 
                      background: hoveredCard === item.id ? `linear-gradient(135deg, ${item.color}, ${item.color}dd)`: 'linear-gradient(135deg, #14b8a6, #0891b2)', 
                      transform: hoveredCard === item.id ? 'scale(1.05)' : 'scale(1)', 
                      boxShadow: hoveredCard === item.id ? `0 8px 25px ${item.color}40` : '0 4px 15px rgba(20, 184, 166, 0.3)' 
                    }}
                    onClick={() => handleShowModal(item)}
                  >
                    Get Started
                    <ChevronRight size={16} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div style={styles.bottomSection}>
          <div style={styles.trustBadge} data-component="trustBadge">
            <div style={styles.trustItem}>
              <div style={styles.trustDot}></div>
              <span style={styles.trustText}>Trusted by 500+ institutions</span>
            </div>
            <div style={styles.divider}></div>
            <div style={styles.trustItem}>
              <div style={styles.trustDot}></div>
              <span style={styles.trustText}>100k+ students trained</span>
            </div>
          </div>
        </div>

        <style>{`
          @media (max-width: 800px) {
            [data-component="container"] {
              padding: 4rem 1rem !important;
            }
            [data-component="grid"] {
              grid-template-columns: repeat(auto-fit, minmax(320px, 1fr)) !important;
              gap: 1.5rem !important;
            }
            [data-component="card"] {
              width: 95% !important;
              margin-left: auto !important;
              margin-right: auto !important;
            }
          }
          
          @media (max-width: 768px) {
            [data-component="container"] {
              padding: 3rem 0.5rem !important;
            }
            [data-component="imageContainer"] {
              width: 90% !important;
              margin: 2em auto 0 !important;
              height: auto !important;
            }
            [data-component="grid"] {
              grid-template-columns: 1fr !important;
              gap: 1.5rem !important;
              padding: 0 0.5rem !important;
            }
            [data-component="cardHeader"] {
              flex-direction: column !important;
              align-items: flex-start !important;
            }
            [data-component="cardFooter"] {
              flex-direction: column !important;
              align-items: stretch !important;
            }
            [data-component="ctaButton"] {
              width: 100% !important;
              justify-content: center !important;
            }
            [data-component="trustBadge"] {
              flex-direction: column !important;
              gap: 1rem !important;
              padding: 1.5rem !important;
            }
            [data-component="featuresGrid"] {
              grid-template-columns: 1fr !important;
            }
          }
          
          @media (max-width: 480px) {
            [data-component="imageContainer"] {
              width: 95% !important;
            }
            [data-component="card"] {
              width: 90% !important;
              padding: 1.5rem !important;
              min-height: 400px !important;
            }
            [data-component="iconContainer"] {
              width: 60px !important;
              height: 60px !important;
            }
          }

          @keyframes bubbleFloat {
            0% { transform: translateY(0px) translateX(0px) rotate(0deg); }
            50% { transform: translateY(-100px) translateX(var(--translateX, 20px)) rotate(180deg); }
            100% { transform: translateY(0px) translateX(0px) rotate(360deg); }
          }
          @keyframes pulse {
            0%, 100% { opacity: 1; transform: scale(1); box-shadow: 0 0 10px rgba(20, 184, 166, 0.5); }
            50% { opacity: 0.7; transform: scale(1.2); box-shadow: 0 0 20px rgba(139, 92, 246, 0.7); }
          }
        `}</style>
      </div>

      <Modal show={showModal} onHide={handleCloseModal} size="lg" centered>
        <Modal.Header closeButton style={{ borderBottom: '1px solid #e2e8f0', padding: '1.5rem' }}>
          <Modal.Title style={{ 
            fontSize: '1.5rem', 
            fontWeight: '700', 
            color: selectedKit?.color || '#1e293b',
            display: 'flex',
            alignItems: 'center',
            gap: '1rem'
          }}>
            {selectedKit && <selectedKit.icon size={28} color={selectedKit.color} />}
            Get Started with {selectedKit?.title} AI Training Kit
          </Modal.Title>
        </Modal.Header>
        
        <Modal.Body style={{ padding: '2rem' }}>
          <Form onSubmit={handleSubmit}>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label style={{ fontWeight: '600', color: '#374151' }}>First Name *</Form.Label>
                  <Form.Control
                    type="text"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    required
                    style={{ 
                      borderRadius: '8px',
                      border: '2px solid #e2e8f0',
                      padding: '0.75rem',
                      fontSize: '0.875rem',
                      transition: 'border-color 0.2s ease'
                    }}
                    onFocus={(e) => e.target.style.borderColor = selectedKit?.color || '#14b8a6'}
                    onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label style={{ fontWeight: '600', color: '#374151' }}>Last Name *</Form.Label>
                  <Form.Control
                    type="text"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleInputChange}
                    required
                    style={{ 
                      borderRadius: '8px',
                      border: '2px solid #e2e8f0',
                      padding: '0.75rem',
                      fontSize: '0.875rem',
                      transition: 'border-color 0.2s ease'
                    }}
                    onFocus={(e) => e.target.style.borderColor = selectedKit?.color || '#14b8a6'}
                    onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label style={{ fontWeight: '600', color: '#374151' }}>Email Address *</Form.Label>
                  <Form.Control
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    style={{ 
                      borderRadius: '8px',
                      border: '2px solid #e2e8f0',
                      padding: '0.75rem',
                      fontSize: '0.875rem',
                      transition: 'border-color 0.2s ease'
                    }}
                    onFocus={(e) => e.target.style.borderColor = selectedKit?.color || '#14b8a6'}
                    onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label style={{ fontWeight: '600', color: '#374151' }}>Phone Number</Form.Label>
                  <Form.Control
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    style={{ 
                      borderRadius: '8px',
                      border: '2px solid #e2e8f0',
                      padding: '0.75rem',
                      fontSize: '0.875rem',
                      transition: 'border-color 0.2s ease'
                    }}
                    onFocus={(e) => e.target.style.borderColor = selectedKit?.color || '#14b8a6'}
                    onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Label style={{ fontWeight: '600', color: '#374151' }}>Institution Name *</Form.Label>
                  <Form.Control
                    type="text"
                    name="institutionName"
                    value={formData.institutionName}
                    onChange={handleInputChange}
                    required
                    style={{ 
                      borderRadius: '8px',
                      border: '2px solid #e2e8f0',
                      padding: '0.75rem',
                      fontSize: '0.875rem',
                      transition: 'border-color 0.2s ease',
                    }}
                    onFocus={(e) => e.target.style.borderColor = selectedKit?.color || '#14b8a6'}
                    onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={8}>
                <Form.Group className="mb-3">
                  <Form.Label style={{ fontWeight: '600', color: '#374151' }}>Your Position *</Form.Label>
                  <Form.Control
                    type="text"
                    name="position"
                    value={formData.position}
                    onChange={handleInputChange}
                    placeholder="e.g., Principal, HoD, Chairman, etc"
                    required
                    style={{ 
                      borderRadius: '8px',
                      border: '2px solid #e2e8f0',
                      padding: '0.75rem',
                      fontSize: '0.875rem',
                      transition: 'border-color 0.2s ease'
                    }}
                    onFocus={(e) => e.target.style.borderColor = selectedKit?.color || '#14b8a6'}
                    onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label style={{ fontWeight: '600', color: '#374151' }}>Institution Type *</Form.Label>
                  <Form.Select
                    name="institutionType"
                    value={formData.institutionType}
                    onChange={handleInputChange}
                    required
                    style={{ 
                      borderRadius: '8px',
                      border: '2px solid #e2e8f0',
                      padding: '0.75rem',
                      fontSize: '0.875rem',
                      transition: 'border-color 0.2s ease',
                    }}
                    onFocus={(e) => e.target.style.borderColor = selectedKit?.color || '#14b8a6'}
                    onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
                  >
                    <option value="">Select Type</option>
                    <option value="university">University</option>
                    <option value="college">College</option>
                    <option value="school">School</option>
                    <option value="training-center">Training Center</option>
                    <option value="other">Other</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-4">
              <Form.Label style={{ fontWeight: '600', color: '#374151' }}>Specific Requirements or Questions</Form.Label>
              <Form.Control
                as="textarea"
                rows={4}
                name="specificRequirements"
                value={formData.specificRequirements}
                onChange={handleInputChange}
                placeholder="Tell us about your specific needs, goals, or any questions you have about the AI training program..."
                style={{ 
                  borderRadius: '8px',
                  border: '2px solid #e2e8f0',
                  padding: '0.75rem',
                  fontSize: '0.875rem',
                  transition: 'border-color 0.2s ease',
                  resize: 'vertical'
                }}
                onFocus={(e) => e.target.style.borderColor = selectedKit?.color || '#14b8a6'}
                onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
              />
            </Form.Group>

            <div style={{ 
              display: 'flex', 
              gap: '1rem', 
              justifyContent: 'flex-end',
              paddingTop: '1rem',
              borderTop: '1px solid #e2e8f0'
            }}>
              <Button 
                variant="outline-secondary" 
                onClick={handleCloseModal}
                style={{
                  padding: '0.75rem 1.5rem',
                  borderRadius: '8px',
                  fontWeight: '600',
                  border: '2px solid #e2e8f0',
                }}
              >
                Cancel
              </Button>
              <Button 
                type="submit"
                style={{
                  padding: '0.75rem 2rem',
                  borderRadius: '8px',
                  fontWeight: '600',
                  background: selectedKit?.color || '#14b8a6',
                  border: 'none',
                  boxShadow: `0 4px 15px ${selectedKit?.color}30` || '0 4px 15px rgba(20, 184, 166, 0.3)'
                }}
              >
                Submit Request
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default AITrainerKitSection